import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Shield, ArrowUpDown, DollarSign, Layers, Info, TrendingDown, SlidersHorizontal, Activity, Zap, Grid3X3 } from 'lucide-react';
import { GreeksExplainer } from '@/components/GreeksExplainer';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer, Area, AreaChart, Legend,
  BarChart, Bar, Cell,
} from 'recharts';

// ─── Premium estimator (simplified Black-Scholes proxy) ────────────

function estimatePremium(spot: number, strike: number, vol: number, days: number, type: 'put' | 'call'): number {
  const T = days / 365;
  const intrinsic = type === 'put' ? Math.max(strike - spot, 0) : Math.max(spot - strike, 0);
  const timeValue = spot * vol * Math.sqrt(T) * 0.4;
  return Math.max(intrinsic + timeValue, 0.01);
}

// ─── Payoff data generators (parametric) ───────────────────────────

function generateProtectivePutPayoff(spot: number, strikePct: number, vol: number, days: number) {
  const strike = spot * strikePct;
  const premium = estimatePremium(spot, strike, vol, days, 'put');
  const lo = Math.round(spot * 0.6);
  const hi = Math.round(spot * 1.5);
  const points = [];
  for (let price = lo; price <= hi; price += 1) {
    const stockOnly = price - spot;
    const putOptionValue = Math.max(strike - price, 0);
    const combined = stockOnly + putOptionValue - premium;
    points.push({
      price,
      stockOnly,
      combined,
      stockValue: price,
      putValue: Math.round((putOptionValue - premium) * 100) / 100,
      combinedValue: Math.round((price + putOptionValue - premium) * 100) / 100,
    });
  }
  return { points, strike: Math.round(strike * 100) / 100, premium: Math.round(premium * 100) / 100 };
}

function generateCollarPayoff(spot: number, putPct: number, callPct: number, vol: number, days: number) {
  const putStrike = spot * putPct;
  const callStrike = spot * callPct;
  const putPrem = estimatePremium(spot, putStrike, vol, days, 'put');
  const callPrem = estimatePremium(spot, callStrike, vol, days, 'call');
  const netPremium = putPrem - callPrem;
  const lo = Math.round(spot * 0.6);
  const hi = Math.round(spot * 1.5);
  const points = [];
  for (let price = lo; price <= hi; price += 1) {
    const stockOnly = price - spot;
    const putPayoff = Math.max(putStrike - price, 0);
    const callPayoff = -Math.max(price - callStrike, 0);
    const combined = stockOnly + putPayoff + callPayoff - netPremium;
    const collarOptionPnL = putPayoff + callPayoff - netPremium;
    points.push({
      price,
      stockOnly,
      combined,
      stockValue: price,
      combinedValue: Math.round((price + putPayoff + callPayoff - netPremium) * 100) / 100,
      collarPnL: Math.round(collarOptionPnL * 100) / 100,
    });
  }
  return {
    points,
    putStrike: Math.round(putStrike * 100) / 100,
    callStrike: Math.round(callStrike * 100) / 100,
    netPremium: Math.round(netPremium * 100) / 100,
  };
}

function generateCoveredCallPayoff(spot: number, callPct: number, vol: number, days: number) {
  const strike = spot * callPct;
  const premium = estimatePremium(spot, strike, vol, days, 'call');
  const lo = Math.round(spot * 0.6);
  const hi = Math.round(spot * 1.5);
  const points = [];
  for (let price = lo; price <= hi; price += 1) {
    const stockOnly = price - spot;
    const combined = stockOnly - Math.max(price - strike, 0) + premium;
    points.push({ price, stockOnly, combined });
  }
  return { points, strike: Math.round(strike * 100) / 100, premium: Math.round(premium * 100) / 100 };
}

function generateBearPutSpreadPayoff(spot: number, highPct: number, lowPct: number, vol: number, days: number) {
  const highStrike = spot * highPct;
  const lowStrike = spot * lowPct;
  const longPrem = estimatePremium(spot, highStrike, vol, days, 'put');
  const shortPrem = estimatePremium(spot, lowStrike, vol, days, 'put');
  const netPremium = longPrem - shortPrem;
  const lo = Math.round(spot * 0.6);
  const hi = Math.round(spot * 1.5);
  const points = [];
  for (let price = lo; price <= hi; price += 1) {
    const stockOnly = price - spot;
    const spreadPayoff = Math.max(highStrike - price, 0) - Math.max(lowStrike - price, 0) - netPremium;
    points.push({
      price,
      stockOnly,
      combined: stockOnly + spreadPayoff,
      stockValue: price,
      combinedValue: Math.round((price + spreadPayoff) * 100) / 100,
      spreadPnL: Math.round(spreadPayoff * 100) / 100,
    });
  }
  return {
    points,
    highStrike: Math.round(highStrike * 100) / 100,
    lowStrike: Math.round(lowStrike * 100) / 100,
    netPremium: Math.round(netPremium * 100) / 100,
  };
}

// ─── Time decay data ───────────────────────────────────────────────

function generateThetaDecay(spot: number, vol: number, totalDays: number) {
  const data = [];
  const step = Math.max(1, Math.round(totalDays / 60));
  for (let day = totalDays; day >= 0; day -= step) {
    const atm = spot * vol * Math.sqrt(day / 365) * 0.4;
    const otm5 = spot * 0.95 * vol * Math.sqrt(day / 365) * 0.35;
    const otm10 = spot * 0.90 * vol * Math.sqrt(day / 365) * 0.30;
    data.push({
      day: totalDays - day,
      atm: Math.round(Math.max(atm, 0.01) * 100) / 100,
      otm5: Math.round(Math.max(otm5, 0.01) * 100) / 100,
      otm10: Math.round(Math.max(otm10, 0.01) * 100) / 100,
    });
  }
  // Ensure last point (day=totalDays) is included
  if (data[data.length - 1]?.day !== totalDays) {
    data.push({ day: totalDays, atm: 0.01, otm5: 0.01, otm10: 0.01 });
  }
  return data;
}

// IV overlay levels for volatility impact comparison
const IV_LEVELS = [
  { key: 'iv15', label: '15%', vol: 0.15, color: '#22c55e' },
  { key: 'iv25', label: '25%', vol: 0.25, color: '#3b82f6' },
  { key: 'iv40', label: '40%', vol: 0.40, color: '#f59e0b' },
  { key: 'iv60', label: '60%', vol: 0.60, color: '#ef4444' },
  { key: 'iv80', label: '80%', vol: 0.80, color: '#a855f7' },
];

function generateIVOverlayData(spot: number, totalDays: number, activeIVs: string[]) {
  const data = [];
  const step = Math.max(1, Math.round(totalDays / 60));
  for (let day = totalDays; day >= 0; day -= step) {
    const entry: Record<string, number> = { day: totalDays - day };
    for (const iv of IV_LEVELS) {
      if (activeIVs.includes(iv.key)) {
        const premium = spot * iv.vol * Math.sqrt(day / 365) * 0.4;
        entry[iv.key] = Math.round(Math.max(premium, 0.01) * 100) / 100;
      }
    }
    data.push(entry);
  }
  if (data[data.length - 1]?.day !== totalDays) {
    const lastEntry: Record<string, number> = { day: totalDays };
    for (const iv of IV_LEVELS) {
      if (activeIVs.includes(iv.key)) lastEntry[iv.key] = 0.01;
    }
    data.push(lastEntry);
  }
  return data;
}

// ─── Chart wrapper ─────────────────────────────────────────────────

function PayoffChart({ data, lines, title, annotations, spot, yLabel }: {
  data: any[];
  lines: { key: string; color: string; name: string; dashed?: boolean }[];
  title: string;
  spot: number;
  yLabel?: string;
  annotations?: { x: number; label: string; color: string }[];
}) {
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-popover border border-border rounded-xl shadow-xl p-3 min-w-[200px]">
        <p className="text-xs font-semibold text-foreground mb-1.5">Stock Price: ${label}</p>
        {payload.map((p: any, i: number) => (
          <div key={i} className="flex justify-between items-center gap-4 py-0.5">
            <div className="flex items-center gap-2">
              <div className="w-3 h-1.5 rounded-full" style={{ backgroundColor: p.color }} />
              <span className="text-xs text-muted-foreground">{p.name}</span>
            </div>
            <span className={`text-xs font-bold font-mono ${p.value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>
              ${p.value.toFixed(2)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-bold text-foreground">{title}</h4>
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={data} margin={{ top: 15, right: 25, bottom: 25, left: 15 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.12} />
          <XAxis
            dataKey="price"
            tick={{ fontSize: 11, fontWeight: 500 }}
            label={{ value: 'Stock Price at Expiry', position: 'insideBottom', offset: -15, style: { fontSize: 12, fontWeight: 600 } }}
          />
          <YAxis
            tick={{ fontSize: 11, fontWeight: 500 }}
            tickFormatter={(v: number) => `$${v}`}
            label={{ value: yLabel || 'P&L ($)', angle: -90, position: 'insideLeft', offset: 5, style: { fontSize: 12, fontWeight: 600 } }}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} strokeDasharray="6 3" opacity={0.6} />
          {yLabel === 'Value ($)' && (
            <ReferenceLine
              y={spot}
              stroke="hsl(var(--primary))"
              strokeWidth={1}
              strokeDasharray="8 4"
              opacity={0.4}
              label={{ value: `Current $${spot}`, position: 'right', style: { fontSize: 10, fontWeight: 600, fill: 'hsl(var(--primary))' } }}
            />
          )}
          <ReferenceLine
            x={spot}
            stroke="hsl(var(--primary))"
            strokeDasharray="4 4"
            strokeWidth={1.5}
            opacity={0.5}
            label={{ value: `Spot $${spot}`, position: 'top', style: { fontSize: 11, fontWeight: 700, fill: 'hsl(var(--primary))' } }}
          />
          {annotations?.map((a, i) => (
            <ReferenceLine
              key={i}
              x={a.x}
              stroke={a.color}
              strokeDasharray="3 4"
              strokeWidth={1.5}
              label={{ value: a.label, position: 'top', style: { fontSize: 10, fontWeight: 700, fill: a.color } }}
            />
          ))}
          {lines.map(l => (
            <Line
              key={l.key}
              type="monotone"
              dataKey={l.key}
              stroke={l.color}
              name={l.name}
              strokeWidth={l.dashed ? 2 : 3}
              strokeDasharray={l.dashed ? '8 4' : undefined}
              dot={false}
              activeDot={{ r: 5, strokeWidth: 2, fill: l.color }}
            />
          ))}
          <Legend
            wrapperStyle={{ paddingTop: 12 }}
            iconType="plainline"
            iconSize={24}
            formatter={(value: string) => (
              <span style={{ fontSize: 13, fontWeight: 600, marginLeft: 4 }}>{value}</span>
            )}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Slider control row ────────────────────────────────────────────

function SimSlider({ label, value, min, max, step, onChange, suffix = '' }: {
  label: string; value: number; min: number; max: number; step: number; onChange: (v: number) => void; suffix?: string;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <span className="text-xs font-mono font-bold text-primary">{value}{suffix}</span>
      </div>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} className="w-full" />
    </div>
  );
}

// ─── Strategy metadata ─────────────────────────────────────────────

const strategies = [
  {
    id: 'protective-put',
    name: 'Protective Put',
    icon: Shield,
    type: 'Single',
    description: 'Buy a put option on a stock you own to limit downside risk. Acts as portfolio insurance — you pay a premium for the right to sell at the strike price regardless of how far the stock falls.',
    mechanics: [
      'You own shares at the spot price',
      'Buy a put contract at the chosen strike (below spot)',
      'If stock drops below strike → exercise put, limit your loss',
      'If stock rises → put expires worthless, you keep all upside minus premium',
    ],
    when: 'You hold a stock with significant gains and want insurance against a crash without selling.',
    pros: ['Unlimited upside preserved', 'Defined maximum loss', 'No obligation to sell shares', 'Works for any position size (1 contract = 100 shares)'],
    cons: ['Premium cost reduces returns (typically 2-5% per quarter)', 'Time decay erodes value if stock stays flat', 'Must be rolled/renewed periodically', 'Expensive in high-volatility environments'],
    riskReduction: 'High', costLevel: 'Medium–High', complexity: 'Low',
    bestFor: 'Large concentrated positions with unrealised gains',
    color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/20',
  },
  {
    id: 'collar',
    name: 'Collar (Put + Covered Call)',
    icon: ArrowUpDown,
    type: 'Combination',
    description: 'Buy a protective put AND sell a covered call simultaneously. The call premium offsets the put cost, creating a "corridor" for your position.',
    mechanics: [
      'You own shares at the spot price',
      'Buy a put (downside floor) and sell a call (upside cap)',
      'Call premium offsets put cost → near-zero net cost',
      'Your P&L is locked between floor (put) and cap (call)',
    ],
    when: 'You want downside protection at zero or minimal cost, and are willing to cap your upside.',
    pros: ['Near-zero cost (call premium funds put purchase)', 'Defined risk range — floor and ceiling', 'Great for locked-in gains you want to protect cheaply', 'Can be structured as zero-cost collar'],
    cons: ['Upside is capped — stock gains above call strike are forfeited', 'More complex to manage (two legs)', 'Assignment risk on the call side if stock rallies', 'Opportunity cost if stock surges'],
    riskReduction: 'High', costLevel: 'Low (often zero)', complexity: 'Medium',
    bestFor: 'Positions where you want cheap protection and accept limited upside',
    color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/20',
  },
  {
    id: 'covered-call',
    name: 'Covered Call',
    icon: DollarSign,
    type: 'Single',
    description: 'Sell call options against shares you own to generate income. You receive premium upfront but cap your upside at the strike price.',
    mechanics: [
      'You own shares at the spot price',
      'Sell a call at strike above spot → receive premium',
      'If stock stays below strike → keep shares + premium income',
      'If stock rises past strike → shares called away, gain capped',
    ],
    when: 'You have a neutral-to-slightly-bullish outlook and want to generate income from existing positions.',
    pros: ['Generates immediate income (premium received)', 'Reduces cost basis of position', 'Works well in sideways markets', 'Simple to execute — single leg'],
    cons: ['Upside capped at strike price', 'Minimal downside protection (only premium buffer)', 'Requires 100+ shares per contract', 'Risk of assignment if stock rallies past strike'],
    riskReduction: 'Low', costLevel: 'Income-generating', complexity: 'Low',
    bestFor: 'Large positions in stable stocks where you want yield',
    color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/20',
  },
  {
    id: 'put-spread',
    name: 'Bear Put Spread',
    icon: Layers,
    type: 'Combination',
    description: 'Buy a put at a higher strike and sell a put at a lower strike. Creates a protection zone between the two strikes at reduced cost.',
    mechanics: [
      'You own shares at the spot price',
      'Buy a put at a higher strike (near spot)',
      'Sell a put at a lower strike → reduces net cost',
      'Protection zone covers the spread between strikes only',
    ],
    when: 'You want partial downside protection at lower cost than a full protective put.',
    pros: ['Lower cost than outright protective put', 'Defined risk and reward', 'Less capital required', 'Works well for moderate-decline scenarios'],
    cons: ['Protection is capped — only covers the spread range', 'Does not protect against a crash below the lower strike', 'Two legs to manage', 'Less effective in extreme sell-offs'],
    riskReduction: 'Medium', costLevel: 'Low–Medium', complexity: 'Medium',
    bestFor: 'Cost-conscious hedging for moderate declines',
    color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/20',
  },
];

const combinationStrategies = [
  { name: 'Collar + Covered Call on other positions', description: 'Collar high-risk positions while writing covered calls on stable ones to fund the overall hedge.', score: '★★★★★', risk: 'Balanced' },
  { name: 'Protective Put (ATM) + Bear Put Spread (OTM)', description: 'Full protection on top positions, cheaper spread hedging on smaller ones.', score: '★★★★', risk: 'Conservative' },
  { name: 'Covered Calls across portfolio + tail-risk puts', description: 'Income from calls funds deep OTM puts for black-swan protection.', score: '★★★★', risk: 'Income-focused' },
];

// ─── Main Component ────────────────────────────────────────────────

export function HedgeStrategyGuide() {
  // Simulator parameters
  const [spot, setSpot] = useState(100);
  const [vol, setVol] = useState(0.25);
  const [days, setDays] = useState(90);
  const [thetaDays, setThetaDays] = useState(90);
  const [thetaCursor, setThetaCursor] = useState(30);
  const [putStrikePct, setPutStrikePct] = useState(0.95);
  const [callStrikePct, setCallStrikePct] = useState(1.10);
  const [spreadLowPct, setSpreadLowPct] = useState(0.90);
  const [activeIVs, setActiveIVs] = useState<string[]>(['iv15', 'iv25', 'iv40', 'iv60']);

  // Compute all chart data reactively
  const putData = useMemo(() => generateProtectivePutPayoff(spot, putStrikePct, vol, days), [spot, putStrikePct, vol, days]);
  const collarData = useMemo(() => generateCollarPayoff(spot, putStrikePct, callStrikePct, vol, days), [spot, putStrikePct, callStrikePct, vol, days]);
  const ccData = useMemo(() => generateCoveredCallPayoff(spot, callStrikePct, vol, days), [spot, callStrikePct, vol, days]);
  const spreadData = useMemo(() => generateBearPutSpreadPayoff(spot, 1.0, spreadLowPct, vol, days), [spot, spreadLowPct, vol, days]);
  const thetaData = useMemo(() => generateThetaDecay(spot, vol, thetaDays), [spot, vol, thetaDays]);
  const ivOverlayData = useMemo(() => generateIVOverlayData(spot, thetaDays, activeIVs), [spot, thetaDays, activeIVs]);

  const toggleIV = (key: string) => {
    setActiveIVs(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  // Dynamic cost comparison
  const costComparisonData = useMemo(() => {
    const putPrem = estimatePremium(spot, spot, vol, days, 'put');
    const putOtm = estimatePremium(spot, spot * 0.95, vol, days, 'put');
    const collarPut = estimatePremium(spot, spot * putStrikePct, vol, days, 'put');
    const collarCall = estimatePremium(spot, spot * callStrikePct, vol, days, 'call');
    const ccPrem = estimatePremium(spot, spot * callStrikePct, vol, days, 'call');
    const spreadHigh = estimatePremium(spot, spot, vol, days, 'put');
    const spreadLow = estimatePremium(spot, spot * spreadLowPct, vol, days, 'put');
    return [
      { strategy: 'Protective Put (ATM)', cost: Math.round(putPrem / spot * 10000) / 100 },
      { strategy: 'Protective Put (5% OTM)', cost: Math.round(putOtm / spot * 10000) / 100 },
      { strategy: 'Collar', cost: Math.round((collarPut - collarCall) / spot * 10000) / 100 },
      { strategy: 'Bear Put Spread', cost: Math.round((spreadHigh - spreadLow) / spot * 10000) / 100 },
      { strategy: 'Covered Call', cost: -Math.round(ccPrem / spot * 10000) / 100 },
    ];
  }, [spot, vol, days, putStrikePct, callStrikePct, spreadLowPct]);

  const chartConfigs: Record<string, { data: any[]; lines: any[]; title: string; annotations?: any[]; stats?: string; yLabel?: string }> = {
    'protective-put': {
      data: putData.points,
      title: `Position Value: Stock vs. Stock + Put (Strike $${putData.strike})`,
      lines: [
        { key: 'stockValue', color: '#94a3b8', name: 'Stock Value', dashed: true },
        { key: 'combinedValue', color: '#3b82f6', name: 'Stock + Put Value' },
        { key: 'putValue', color: '#f97316', name: 'Put Option P&L' },
      ],
      annotations: [{ x: putData.strike, label: `Put $${putData.strike}`, color: '#3b82f6' }],
      stats: `Premium: $${putData.premium}/share | Max Loss: $${(spot - putData.strike + putData.premium).toFixed(2)}/share | Put gains value below $${putData.strike}`,
      yLabel: 'Value ($)',
    },
    collar: {
      data: collarData.points,
      title: `Position Value: Stock vs. Stock + Collar (Put $${collarData.putStrike} / Call $${collarData.callStrike})`,
      lines: [
        { key: 'stockValue', color: '#94a3b8', name: 'Stock Value', dashed: true },
        { key: 'combinedValue', color: '#a855f7', name: 'Stock + Collar Value' },
        { key: 'collarPnL', color: '#f97316', name: 'Collar Options P&L' },
      ],
      annotations: [
        { x: collarData.putStrike, label: `Put $${collarData.putStrike}`, color: '#ef4444' },
        { x: collarData.callStrike, label: `Call $${collarData.callStrike}`, color: '#22c55e' },
      ],
      stats: `Net Premium: ${collarData.netPremium > 0 ? 'Debit' : 'Credit'} $${Math.abs(collarData.netPremium).toFixed(2)}/share | Put gains value below $${collarData.putStrike} | Upside capped at $${collarData.callStrike}`,
      yLabel: 'Value ($)',
    },
    'covered-call': {
      data: ccData.points,
      title: `Payoff: Stock Only vs. Covered Call (Strike $${ccData.strike})`,
      lines: [
        { key: 'stockOnly', color: '#94a3b8', name: 'Stock Only', dashed: true },
        { key: 'combined', color: '#10b981', name: 'Covered Call' },
      ],
      annotations: [{ x: ccData.strike, label: `Call $${ccData.strike}`, color: '#10b981' }],
      stats: `Income: $${ccData.premium}/share | Buffer: ${((ccData.premium / spot) * 100).toFixed(1)}%`,
    },
    'put-spread': {
      data: spreadData.points,
      title: `Position Value: Stock vs. Stock + Bear Put Spread ($${spot} / $${spreadData.lowStrike})`,
      lines: [
        { key: 'stockValue', color: '#94a3b8', name: 'Stock Value', dashed: true },
        { key: 'combinedValue', color: '#f59e0b', name: 'Stock + Spread Value' },
        { key: 'spreadPnL', color: '#f97316', name: 'Spread P&L' },
      ],
      annotations: [
        { x: spot, label: `Long Put $${spot}`, color: '#f59e0b' },
        { x: spreadData.lowStrike, label: `Short Put $${spreadData.lowStrike}`, color: '#ef4444' },
      ],
      stats: `Net Cost: $${spreadData.netPremium}/share | Max Hedge: $${(spot - spreadData.lowStrike - spreadData.netPremium).toFixed(2)}/share | Protection between $${spreadData.lowStrike}–$${spot}`,
      yLabel: 'Value ($)',
    },
  };

  return (
    <div className="space-y-6">
      <Card className="border-primary/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl flex items-center gap-2">
            <Info className="h-5 w-5 text-primary" />
            Hedge Strategy Reference Guide
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Interactive payoff simulator — adjust parameters below to see how each strategy behaves
            under different market conditions. All charts update in real time.
          </p>
        </CardContent>
      </Card>

      {/* ─── Interactive Simulator Controls ─────────────────────────── */}
      <Card className="border-primary/30 bg-primary/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-primary" />
            Payoff Simulator — Adjust Parameters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <SimSlider label="Spot Price" value={spot} min={20} max={500} step={5} onChange={setSpot} suffix="$" />
            <SimSlider label="Volatility (IV)" value={Math.round(vol * 100)} min={10} max={80} step={1} onChange={(v) => setVol(v / 100)} suffix="%" />
            <SimSlider label="Days to Expiry" value={days} min={7} max={365} step={7} onChange={setDays} suffix="d" />
            <SimSlider label="Put Strike %" value={Math.round(putStrikePct * 100)} min={80} max={100} step={1} onChange={(v) => setPutStrikePct(v / 100)} suffix="%" />
            <SimSlider label="Call Strike %" value={Math.round(callStrikePct * 100)} min={100} max={130} step={1} onChange={(v) => setCallStrikePct(v / 100)} suffix="%" />
            <SimSlider label="Spread Low %" value={Math.round(spreadLowPct * 100)} min={75} max={99} step={1} onChange={(v) => setSpreadLowPct(v / 100)} suffix="%" />
          </div>
          <p className="text-xs text-muted-foreground mt-3 italic">
            Spot: ${spot} | IV: {Math.round(vol * 100)}% | Expiry: {days}d | Put: {Math.round(putStrikePct * 100)}% (${(spot * putStrikePct).toFixed(0)}) | Call: {Math.round(callStrikePct * 100)}% (${(spot * callStrikePct).toFixed(0)}) | Spread Low: {Math.round(spreadLowPct * 100)}% (${(spot * spreadLowPct).toFixed(0)})
          </p>
        </CardContent>
      </Card>

      {/* ─── Strategy Tabs with Dynamic Charts ──────────────────────── */}
      <Tabs defaultValue="protective-put" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          {strategies.map(s => (
            <TabsTrigger key={s.id} value={s.id} className="text-xs md:text-sm gap-1">
              <s.icon className="h-3 w-3 hidden md:inline" />
              {s.name.split(' ')[0]}
            </TabsTrigger>
          ))}
        </TabsList>

        {strategies.map(s => {
          const chart = chartConfigs[s.id];
          return (
            <TabsContent key={s.id} value={s.id} className="mt-4">
              <Card className={`${s.borderColor} border`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className={`text-lg flex items-center gap-2 ${s.color}`}>
                      <s.icon className="h-5 w-5" />
                      {s.name}
                    </CardTitle>
                    <div className="flex gap-2">
                      <Badge variant="outline" className={s.bgColor}>{s.type}</Badge>
                      <Badge variant="outline">Complexity: {s.complexity}</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{s.description}</p>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Payoff Chart */}
                  <div className={`p-4 rounded-xl ${s.bgColor} border ${s.borderColor}`}>
                    <PayoffChart {...chart} spot={spot} />
                    {chart.stats && (
                      <p className="text-xs font-mono text-muted-foreground mt-2 text-center">{chart.stats}</p>
                    )}
                  </div>

                  {/* Step-by-step mechanics */}
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <p className="text-sm font-semibold text-foreground mb-2">⚙️ How it works</p>
                    <ol className="space-y-1.5">
                      {s.mechanics.map((step, i) => (
                        <li key={i} className="text-sm text-muted-foreground flex gap-2">
                          <span className="font-mono text-xs text-primary font-bold min-w-[20px]">{i + 1}.</span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div className={`p-3 rounded-lg ${s.bgColor}`}>
                    <p className="text-sm font-medium text-foreground">📌 When to use</p>
                    <p className="text-sm text-muted-foreground">{s.when}</p>
                  </div>

                  {/* Pros & Cons */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-3 font-semibold text-green-600 dark:text-green-400 w-1/2">✅ Pros</th>
                          <th className="text-left py-2 px-3 font-semibold text-red-600 dark:text-red-400 w-1/2">❌ Cons</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.pros.map((pro, i) => (
                          <tr key={i} className="border-b border-border/30">
                            <td className="py-2 px-3 text-green-700 dark:text-green-300 bg-green-500/5">{pro}</td>
                            <td className="py-2 px-3 text-red-700 dark:text-red-300 bg-red-500/5">{s.cons[i] || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-muted/40 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Risk Reduction</p>
                      <p className="text-sm font-bold text-foreground">{s.riskReduction}</p>
                    </div>
                    <div className="p-3 bg-muted/40 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Cost Level</p>
                      <p className="text-sm font-bold text-foreground">{s.costLevel}</p>
                    </div>
                    <div className="p-3 bg-muted/40 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Best For</p>
                      <p className="text-xs font-medium text-foreground">{s.bestFor}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* ─── Cross-Strategy Charts ──────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-destructive" />
              Time Decay — Put Premium Over {thetaDays} Days
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Controls */}
            <div className="grid grid-cols-2 gap-4">
              <SimSlider label="Expiry (days)" value={thetaDays} min={30} max={365} step={5} onChange={(v) => { setThetaDays(v); if (thetaCursor > v) setThetaCursor(v); }} />
              <SimSlider label="Day cursor" value={thetaCursor} min={0} max={thetaDays} step={1} onChange={setThetaCursor} />
            </div>

            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={thetaData} margin={{ top: 5, right: 15, bottom: 15, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="day" label={{ value: 'Days Elapsed', position: 'insideBottom', offset: -5, style: { fontSize: 11 } }} tick={{ fontSize: 10 }} />
                <YAxis label={{ value: 'Premium ($)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <ReferenceLine
                  x={thetaCursor}
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  label={{ value: `Day ${thetaCursor}`, position: 'top', style: { fontSize: 10, fontWeight: 700, fill: 'hsl(var(--primary))' } }}
                />
                <Area type="monotone" dataKey="atm" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} name={`ATM ($${spot})`} strokeWidth={2} />
                <Area type="monotone" dataKey="otm5" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} name={`5% OTM ($${(spot * 0.95).toFixed(0)})`} strokeWidth={2} />
                <Area type="monotone" dataKey="otm10" stroke="#ef4444" fill="#ef4444" fillOpacity={0.08} name={`10% OTM ($${(spot * 0.90).toFixed(0)})`} strokeWidth={2} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </AreaChart>
            </ResponsiveContainer>

            {/* Cursor readout */}
            {(() => {
              const daysLeft = thetaDays - thetaCursor;
              const atmVal = spot * vol * Math.sqrt(daysLeft / 365) * 0.4;
              const otm5Val = spot * 0.95 * vol * Math.sqrt(daysLeft / 365) * 0.35;
              const otm10Val = spot * 0.90 * vol * Math.sqrt(daysLeft / 365) * 0.30;
              const atmStart = spot * vol * Math.sqrt(thetaDays / 365) * 0.4;
              const otm5Start = spot * 0.95 * vol * Math.sqrt(thetaDays / 365) * 0.35;
              const otm10Start = spot * 0.90 * vol * Math.sqrt(thetaDays / 365) * 0.30;
              return (
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div className="p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-center">
                    <p className="text-[10px] text-muted-foreground uppercase">ATM Premium</p>
                    <p className="text-sm font-bold text-blue-500">${Math.max(atmVal, 0.01).toFixed(2)}</p>
                    <p className="text-[10px] text-destructive">−${(atmStart - Math.max(atmVal, 0.01)).toFixed(2)} decay</p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-center">
                    <p className="text-[10px] text-muted-foreground uppercase">5% OTM</p>
                    <p className="text-sm font-bold text-yellow-500">${Math.max(otm5Val, 0.01).toFixed(2)}</p>
                    <p className="text-[10px] text-destructive">−${(otm5Start - Math.max(otm5Val, 0.01)).toFixed(2)} decay</p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-center">
                    <p className="text-[10px] text-muted-foreground uppercase">10% OTM</p>
                    <p className="text-sm font-bold text-red-500">${Math.max(otm10Val, 0.01).toFixed(2)}</p>
                    <p className="text-[10px] text-destructive">−${(otm10Start - Math.max(otm10Val, 0.01)).toFixed(2)} decay</p>
                  </div>
                </div>
              );
            })()}

            <p className="text-xs text-muted-foreground">
              Premium decays fastest in the final 30 days. Roll hedges 2-3 weeks before expiry.
              {thetaCursor > thetaDays - 30 && (
                <span className="text-destructive font-semibold"> ⚠️ You are in the accelerated decay zone!</span>
              )}
            </p>

            {/* IV Impact Overlay */}
            <div className="mt-6 pt-4 border-t border-border">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h4 className="text-sm font-bold text-foreground flex items-center gap-2">
                  📊 Volatility Impact — ATM Premium by IV Level
                </h4>
                <div className="flex gap-1.5 flex-wrap">
                  {IV_LEVELS.map(iv => (
                    <button
                      key={iv.key}
                      onClick={() => toggleIV(iv.key)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-all ${
                        activeIVs.includes(iv.key)
                          ? 'border-foreground/30 bg-foreground/10 text-foreground'
                          : 'border-border bg-muted/30 text-muted-foreground opacity-50'
                      }`}
                      style={activeIVs.includes(iv.key) ? { borderColor: iv.color, color: iv.color, backgroundColor: `${iv.color}15` } : {}}
                    >
                      IV {iv.label}
                    </button>
                  ))}
                </div>
              </div>

              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={ivOverlayData} margin={{ top: 5, right: 15, bottom: 15, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis dataKey="day" label={{ value: 'Days Elapsed', position: 'insideBottom', offset: -5, style: { fontSize: 11 } }} tick={{ fontSize: 10 }} />
                  <YAxis label={{ value: 'ATM Premium ($)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    formatter={(value: number, name: string) => {
                      const iv = IV_LEVELS.find(l => l.key === name);
                      return [`$${value.toFixed(2)}`, iv ? `IV ${iv.label}` : name];
                    }}
                  />
                  <ReferenceLine
                    x={thetaCursor}
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                  />
                  {IV_LEVELS.filter(iv => activeIVs.includes(iv.key)).map(iv => (
                    <Area
                      key={iv.key}
                      type="monotone"
                      dataKey={iv.key}
                      stroke={iv.color}
                      fill={iv.color}
                      fillOpacity={0.08}
                      strokeWidth={2}
                      name={iv.key}
                      dot={false}
                    />
                  ))}
                  <Legend
                    wrapperStyle={{ fontSize: 11 }}
                    formatter={(value: string) => {
                      const iv = IV_LEVELS.find(l => l.key === value);
                      return iv ? `IV ${iv.label}` : value;
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>

              {/* IV comparison readout at cursor */}
              <div className="grid grid-cols-5 gap-2 mt-3">
                {IV_LEVELS.filter(iv => activeIVs.includes(iv.key)).map(iv => {
                  const daysLeft = thetaDays - thetaCursor;
                  const premNow = spot * iv.vol * Math.sqrt(Math.max(daysLeft, 0) / 365) * 0.4;
                  const premStart = spot * iv.vol * Math.sqrt(thetaDays / 365) * 0.4;
                  const decayPct = premStart > 0 ? ((premStart - premNow) / premStart * 100) : 0;
                  return (
                    <div key={iv.key} className="p-2 rounded-lg border text-center" style={{ borderColor: `${iv.color}40`, backgroundColor: `${iv.color}08` }}>
                      <p className="text-[10px] text-muted-foreground uppercase font-semibold">IV {iv.label}</p>
                      <p className="text-sm font-bold" style={{ color: iv.color }}>${Math.max(premNow, 0.01).toFixed(2)}</p>
                      <p className="text-[10px] text-destructive">−{decayPct.toFixed(0)}% decayed</p>
                    </div>
                  );
                })}
              </div>

              <p className="text-xs text-muted-foreground mt-2 italic">
                Higher IV = more expensive premiums but also more time value to lose. Low-IV environments are cheaper to hedge in.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-primary" />
              Cost vs. Protection (% of Position)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={costComparisonData} margin={{ top: 5, right: 15, bottom: 15, left: 5 }} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis type="number" label={{ value: 'Cost (%)', position: 'insideBottom', offset: -5, style: { fontSize: 11 } }} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="strategy" width={140} tick={{ fontSize: 9 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="cost" name="Cost (%)">
                  {costComparisonData.map((entry, i) => (
                    <Cell key={i} fill={entry.cost < 0 ? '#22c55e' : entry.cost < 1 ? '#a855f7' : entry.cost < 3.5 ? '#f59e0b' : '#3b82f6'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-muted-foreground mt-2">
              Negative = income. Values update with simulator parameters above.
            </p>
          </CardContent>
        </Card>

        {/* Vega Sensitivity Chart */}
        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              Vega Sensitivity — Premium Change per 1% IV Move
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Shows how much the ATM put premium changes for each 1% increase in implied volatility at different IV levels and days to expiry.
            </p>
            {(() => {
              const moneyLevels = [
                { key: 'itm5', label: '5% ITM', factor: 1.05, color: '#22c55e' },
                { key: 'atm', label: 'ATM', factor: 1.0, color: '#3b82f6' },
                { key: 'otm5', label: '5% OTM', factor: 0.95, color: '#f59e0b' },
                { key: 'otm10', label: '10% OTM', factor: 0.90, color: '#ef4444' },
                { key: 'otm15', label: '15% OTM', factor: 0.85, color: '#a855f7' },
              ];
              // Generate vega data: for each IV level (10% to 80%), compute premium change per +1% IV
              const vegaData = [];
              for (let ivPct = 10; ivPct <= 80; ivPct += 2) {
                const iv = ivPct / 100;
                const ivUp = (ivPct + 1) / 100;
                const entry: Record<string, number> = { iv: ivPct };
                for (const ml of moneyLevels) {
                  const strike = spot * ml.factor;
                  const premBase = estimatePremium(spot, strike, iv, days, 'put');
                  const premUp = estimatePremium(spot, strike, ivUp, days, 'put');
                  entry[ml.key] = Math.round((premUp - premBase) * 100) / 100;
                }
                vegaData.push(entry);
              }
              return (
                <>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={vegaData} margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                      <XAxis
                        dataKey="iv"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v: number) => `${v}%`}
                        label={{ value: 'Implied Volatility', position: 'insideBottom', offset: -10, style: { fontSize: 11, fontWeight: 600 } }}
                      />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v: number) => `$${v.toFixed(2)}`}
                        label={{ value: 'Δ Premium / +1% IV', angle: -90, position: 'insideLeft', offset: 5, style: { fontSize: 11, fontWeight: 600 } }}
                      />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8 }}
                        labelFormatter={(v: number) => `IV: ${v}%`}
                        formatter={(value: number, name: string) => {
                          const ml = moneyLevels.find(m => m.key === name);
                          return [`$${value.toFixed(2)}`, ml?.label || name];
                        }}
                      />
                      {moneyLevels.map(ml => (
                        <Line
                          key={ml.key}
                          type="monotone"
                          dataKey={ml.key}
                          stroke={ml.color}
                          strokeWidth={2.5}
                          dot={false}
                          activeDot={{ r: 4, strokeWidth: 2, fill: ml.color }}
                          name={ml.key}
                        />
                      ))}
                      <ReferenceLine
                        x={Math.round(vol * 100)}
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        strokeDasharray="4 4"
                        label={{ value: `Current IV ${Math.round(vol * 100)}%`, position: 'top', style: { fontSize: 10, fontWeight: 700, fill: 'hsl(var(--primary))' } }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                        formatter={(value: string) => {
                          const ml = moneyLevels.find(m => m.key === value);
                          return ml?.label || value;
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>

                  {/* Vega readout at current IV */}
                  <div className="grid grid-cols-5 gap-2">
                    {moneyLevels.map(ml => {
                      const strike = spot * ml.factor;
                      const premBase = estimatePremium(spot, strike, vol, days, 'put');
                      const premUp = estimatePremium(spot, strike, vol + 0.01, days, 'put');
                      const vega = premUp - premBase;
                      return (
                        <div key={ml.key} className="p-2 rounded-lg border text-center" style={{ borderColor: `${ml.color}40`, backgroundColor: `${ml.color}08` }}>
                          <p className="text-[10px] text-muted-foreground uppercase font-semibold">{ml.label}</p>
                          <p className="text-sm font-bold" style={{ color: ml.color }}>${vega.toFixed(2)}</p>
                          <p className="text-[10px] text-muted-foreground">per +1% IV</p>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-xs text-muted-foreground italic">
                    ATM options have the highest vega — they're most sensitive to volatility changes. Deep OTM options are cheaper but less responsive to IV shifts. Current params: Spot ${spot}, {days}d expiry.
                  </p>
                </>
              );
            })()}
          </CardContent>
        </Card>

        {/* Gamma Exposure Chart */}
        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" />
              Gamma Exposure — Delta Acceleration Near Expiry
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Gamma measures how fast delta changes per $1 stock move. It peaks for ATM options near expiry — small price moves cause large delta swings, creating explosive hedging needs.
            </p>
            {(() => {
              const moneyLevels = [
                { key: 'itm5', label: '5% ITM', strikeFactor: 1.05, color: '#22c55e' },
                { key: 'atm', label: 'ATM', strikeFactor: 1.0, color: '#3b82f6' },
                { key: 'otm5', label: '5% OTM', strikeFactor: 0.95, color: '#f59e0b' },
                { key: 'otm10', label: '10% OTM', strikeFactor: 0.90, color: '#ef4444' },
                { key: 'otm15', label: '15% OTM', strikeFactor: 0.85, color: '#a855f7' },
              ];

              // Approximate gamma: Δdelta / $1 move ≈ [delta(S+0.5) - delta(S-0.5)]
              // delta ≈ -[P(S+0.5) - P(S-0.5)] (finite difference of put price)
              const computeGamma = (strike: number, ivVal: number, dte: number) => {
                const T = Math.max(dte, 0.5) / 365;
                const shift = 0.50;
                const pUp = estimatePremium(spot + shift, strike, ivVal, Math.max(dte, 0.5), 'put');
                const pMid = estimatePremium(spot, strike, ivVal, Math.max(dte, 0.5), 'put');
                const pDown = estimatePremium(spot - shift, strike, ivVal, Math.max(dte, 0.5), 'put');
                // gamma ≈ (P(S+h) - 2P(S) + P(S-h)) / h²
                const gamma = (pUp - 2 * pMid + pDown) / (shift * shift);
                return Math.abs(gamma);
              };

              // Generate data: days to expiry from max down to 1
              const maxDte = Math.min(days, 180);
              const gammaData = [];
              for (let dte = maxDte; dte >= 1; dte -= Math.max(1, Math.round(maxDte / 80))) {
                const entry: Record<string, number> = { dte };
                for (const ml of moneyLevels) {
                  const strike = spot * ml.strikeFactor;
                  entry[ml.key] = Math.round(computeGamma(strike, vol, dte) * 10000) / 10000;
                }
                gammaData.push(entry);
              }
              // Ensure dte=1 is included
              if (gammaData[gammaData.length - 1]?.dte !== 1) {
                const entry: Record<string, number> = { dte: 1 };
                for (const ml of moneyLevels) {
                  entry[ml.key] = Math.round(computeGamma(spot * ml.strikeFactor, vol, 1) * 10000) / 10000;
                }
                gammaData.push(entry);
              }
              gammaData.sort((a, b) => (b.dte as number) - (a.dte as number));

              return (
                <>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={gammaData} margin={{ top: 10, right: 20, bottom: 20, left: 15 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                      <XAxis
                        dataKey="dte"
                        reversed
                        tick={{ fontSize: 10 }}
                        label={{ value: 'Days to Expiry →', position: 'insideBottom', offset: -10, style: { fontSize: 11, fontWeight: 600 } }}
                      />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v: number) => v.toFixed(3)}
                        label={{ value: 'Gamma (Δδ / $1)', angle: -90, position: 'insideLeft', offset: 5, style: { fontSize: 11, fontWeight: 600 } }}
                      />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8 }}
                        labelFormatter={(v: number) => `${v} DTE`}
                        formatter={(value: number, name: string) => {
                          const ml = moneyLevels.find(m => m.key === name);
                          return [value.toFixed(4), ml?.label || name];
                        }}
                      />
                      {moneyLevels.map(ml => (
                        <Line
                          key={ml.key}
                          type="monotone"
                          dataKey={ml.key}
                          stroke={ml.color}
                          strokeWidth={2.5}
                          dot={false}
                          activeDot={{ r: 4, strokeWidth: 2, fill: ml.color }}
                          name={ml.key}
                        />
                      ))}
                      <ReferenceLine
                        x={days}
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        strokeDasharray="4 4"
                        label={{ value: `Current ${days}d`, position: 'top', style: { fontSize: 10, fontWeight: 700, fill: 'hsl(var(--primary))' } }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                        formatter={(value: string) => {
                          const ml = moneyLevels.find(m => m.key === value);
                          return ml?.label || value;
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>

                  {/* Gamma readout at current DTE */}
                  <div className="grid grid-cols-5 gap-2">
                    {moneyLevels.map(ml => {
                      const gamma = computeGamma(spot * ml.strikeFactor, vol, days);
                      const gammaShort = computeGamma(spot * ml.strikeFactor, vol, Math.max(days - 30, 1));
                      const acceleration = gammaShort - gamma;
                      return (
                        <div key={ml.key} className="p-2 rounded-lg border text-center" style={{ borderColor: `${ml.color}40`, backgroundColor: `${ml.color}08` }}>
                          <p className="text-[10px] text-muted-foreground uppercase font-semibold">{ml.label}</p>
                          <p className="text-sm font-bold" style={{ color: ml.color }}>{gamma.toFixed(4)}</p>
                          <p className={`text-[10px] ${acceleration > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                            {acceleration > 0 ? '↑' : '↓'}{Math.abs(acceleration).toFixed(4)} in 30d
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-xs text-muted-foreground italic">
                    ⚠️ ATM gamma spikes exponentially in the last 7–14 days. This is why hedging costs surge near expiry and why rolling early is critical. IV: {Math.round(vol * 100)}%, Spot: ${spot}.
                  </p>
                </>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Delta Surface Heatmap */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Grid3X3 className="h-4 w-4 text-primary" />
            Delta Surface — Put Delta by Stock Price × Days to Expiry
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Each cell shows the approximate put delta at a given stock price and DTE. Delta near −1.0 means the put moves $1 for every $1 stock drop (deep ITM). Near 0 means the put is far OTM and barely moves.
          </p>
          {(() => {
            const strike = spot * putStrikePct;
            // Price axis: 70% to 130% of spot in steps
            const priceSteps: number[] = [];
            for (let pct = 0.70; pct <= 1.30; pct += 0.05) {
              priceSteps.push(Math.round(spot * pct * 100) / 100);
            }
            // DTE axis
            const dteSteps = [1, 3, 7, 14, 21, 30, 45, 60, 90, 120, 180].filter(d => d <= Math.max(days, 30));
            if (!dteSteps.includes(days) && days <= 180) {
              dteSteps.push(days);
              dteSteps.sort((a, b) => a - b);
            }

            // Approximate delta using finite difference: delta ≈ -[P(S+0.5) - P(S-0.5)]
            const computeDelta = (price: number, dte: number) => {
              const h = 0.5;
              const pUp = estimatePremium(price + h, strike, vol, Math.max(dte, 0.5), 'put');
              const pDown = estimatePremium(price - h, strike, vol, Math.max(dte, 0.5), 'put');
              const delta = -(pUp - pDown) / (2 * h);
              return Math.max(-1, Math.min(0, delta));
            };

            // Color interpolation: -1 (deep red) → -0.5 (orange) → 0 (green)
            const deltaColor = (d: number): string => {
              const abs = Math.abs(d);
              if (abs > 0.7) return `rgba(239, 68, 68, ${0.3 + abs * 0.6})`;
              if (abs > 0.4) return `rgba(245, 158, 11, ${0.2 + abs * 0.5})`;
              if (abs > 0.15) return `rgba(59, 130, 246, ${0.15 + abs * 0.4})`;
              return `rgba(34, 197, 94, ${0.1 + abs * 0.3})`;
            };

            const textColor = (d: number): string => {
              const abs = Math.abs(d);
              if (abs > 0.6) return '#fca5a5';
              if (abs > 0.35) return '#fcd34d';
              if (abs > 0.15) return '#93c5fd';
              return '#86efac';
            };

            return (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr>
                        <th className="p-1.5 text-[10px] text-muted-foreground font-semibold border border-border bg-muted/50 sticky left-0 z-10">
                          Price ↓ / DTE →
                        </th>
                        {dteSteps.map(dte => (
                          <th key={dte} className={`p-1.5 text-[10px] font-semibold border border-border text-center ${dte === days ? 'bg-primary/20 text-primary' : 'bg-muted/50 text-muted-foreground'}`}>
                            {dte}d
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {priceSteps.map(price => {
                        const isSpot = Math.abs(price - spot) < spot * 0.03;
                        const isStrike = Math.abs(price - strike) < spot * 0.03;
                        return (
                          <tr key={price}>
                            <td className={`p-1.5 text-[10px] font-mono font-semibold border border-border sticky left-0 z-10 ${isSpot ? 'bg-primary/20 text-primary' : isStrike ? 'bg-amber-500/20 text-amber-500' : 'bg-muted/50 text-muted-foreground'}`}>
                              ${price.toFixed(0)}
                              {isSpot && ' ●'}
                              {isStrike && !isSpot && ' K'}
                            </td>
                            {dteSteps.map(dte => {
                              const delta = computeDelta(price, dte);
                              return (
                                <td
                                  key={dte}
                                  className="p-1 text-center font-mono font-bold border border-border/50"
                                  style={{ backgroundColor: deltaColor(delta), color: textColor(delta), fontSize: 10 }}
                                  title={`Price: $${price}, DTE: ${dte}, Delta: ${delta.toFixed(3)}`}
                                >
                                  {delta.toFixed(2)}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Legend */}
                <div className="flex items-center justify-center gap-4 flex-wrap text-xs">
                  <span className="text-muted-foreground font-semibold">Delta Scale:</span>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: 'rgba(34, 197, 94, 0.3)' }} />
                    <span className="text-muted-foreground">0 (OTM)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: 'rgba(59, 130, 246, 0.35)' }} />
                    <span className="text-muted-foreground">−0.2</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: 'rgba(245, 158, 11, 0.45)' }} />
                    <span className="text-muted-foreground">−0.5 (ATM)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: 'rgba(239, 68, 68, 0.7)' }} />
                    <span className="text-muted-foreground">−1.0 (ITM)</span>
                  </div>
                  <span className="text-muted-foreground">| ● = Spot | K = Strike</span>
                </div>

                <p className="text-xs text-muted-foreground italic">
                  Strike: ${strike.toFixed(0)} ({Math.round(putStrikePct * 100)}% of spot). Near expiry, delta snaps to 0 or −1 — there's no middle ground. This is why gamma risk is highest at expiry for ATM options.
                </p>
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* Combination Strategies */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Layers className="h-5 w-5 text-primary" />
            Recommended Combinations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3 font-semibold text-foreground">Strategy Combination</th>
                  <th className="text-left py-2 px-3 font-semibold text-foreground">Description</th>
                  <th className="text-center py-2 px-3 font-semibold text-foreground">Rating</th>
                  <th className="text-center py-2 px-3 font-semibold text-foreground">Risk Profile</th>
                </tr>
              </thead>
              <tbody>
                {combinationStrategies.map((cs, i) => (
                  <tr key={i} className="border-b border-border/30 hover:bg-muted/30">
                    <td className="py-2 px-3 font-medium text-primary">{cs.name}</td>
                    <td className="py-2 px-3 text-muted-foreground">{cs.description}</td>
                    <td className="py-2 px-3 text-center text-amber-500">{cs.score}</td>
                    <td className="py-2 px-3 text-center">
                      <Badge variant="outline">{cs.risk}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Best Practices */}
      <Card className="bg-muted/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">🛡️ Best Practices</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• <strong className="text-foreground">Don't hedge everything</strong> — Focus on positions &gt;5% of portfolio or with &gt;30% unrealised gains.</li>
            <li>• <strong className="text-foreground">Mind the margin</strong> — Options add margin requirements. Keep total margin utilisation below 50%.</li>
            <li>• <strong className="text-foreground">Time your hedges</strong> — Hedge before known catalysts (earnings, macro events), not after.</li>
            <li>• <strong className="text-foreground">Roll, don't hold to expiry</strong> — Roll hedges 2-3 weeks before expiry to avoid theta bleed.</li>
            <li>• <strong className="text-foreground">Cost efficiency</strong> — Total hedge cost should not exceed 3-5% of portfolio value per year.</li>
            <li>• <strong className="text-foreground">Use combinations</strong> — Covered calls on stable positions can fund protective puts on volatile ones.</li>
            <li>• <strong className="text-foreground">Watch IV</strong> — Buy puts when IV is low (cheap premiums); sell calls when IV is high (rich premiums).</li>
          </ul>
        </CardContent>
      </Card>

      {/* Greeks Section */}
      <GreeksExplainer />
    </div>
  );
}
